import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { PieChart as PieChartIcon } from 'lucide-react';
import type { ComplaintStatus } from '../../api';

interface StatusDistributionChartProps {
  byStatus: Record<ComplaintStatus, number>;
  isLoading?: boolean;
}

const STATUS_COLORS: Record<ComplaintStatus, string> = {
  submitted: '#6366f1', // indigo
  under_review: '#f59e0b', // amber
  approved: '#10b981', // emerald
  rejected: '#ef4444', // red
};

const STATUS_LABELS: Record<ComplaintStatus, string> = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUS_BG_COLORS: Record<ComplaintStatus, string> = {
  submitted: 'bg-indigo-50',
  under_review: 'bg-amber-50',
  approved: 'bg-emerald-50',
  rejected: 'bg-red-50',
};

const STATUS_TEXT_COLORS: Record<ComplaintStatus, string> = {
  submitted: 'text-indigo-600',
  under_review: 'text-amber-600',
  approved: 'text-emerald-600',
  rejected: 'text-red-600',
};

/**
 * Custom tooltip component
 */
function CustomTooltip({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; payload: { percent: number } }> }) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0];
  const percent = Math.round((data.payload.percent || 0) * 100);

  return (
    <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg p-3">
      <p className="text-sm font-semibold text-gray-900">{data.name}</p>
      <p className="text-xs text-gray-500 mt-1">
        {data.value} complaint{data.value !== 1 ? 's' : ''} ({percent}%)
      </p>
    </div>
  );
}

export function StatusDistributionChart({ byStatus, isLoading }: StatusDistributionChartProps) {
  const data = (Object.keys(byStatus) as ComplaintStatus[])
    .map((status) => ({
      name: STATUS_LABELS[status],
      value: byStatus[status],
      color: STATUS_COLORS[status],
      status,
    }))
    .filter((item) => item.value > 0);

  const total = data.reduce((sum, item) => sum + item.value, 0);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="h-4 bg-gray-200 rounded w-32 mb-1 animate-pulse" />
        <div className="h-3 bg-gray-200 rounded w-24 mb-4 animate-pulse" />
        <div className="h-52 flex items-center justify-center">
          <div className="w-36 h-36 rounded-full bg-gray-100 animate-pulse flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-white" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Status Distribution</h3>
        <p className="text-xs text-gray-500 mb-4">Breakdown by complaint status</p>
        <div className="h-52 flex flex-col items-center justify-center text-gray-400">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <PieChartIcon className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-sm font-medium">No complaint data available</p>
          <p className="text-xs mt-1">Data will appear as complaints are submitted</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Status Distribution</h3>
      <p className="text-xs text-gray-500 mb-4">Breakdown by complaint status</p>

      {/* Chart with center label */}
      <div className="h-52 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={85}
              paddingAngle={3}
              dataKey="value"
              strokeWidth={0}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  className="transition-opacity hover:opacity-80"
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Center label overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">{total}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
        </div>
      </div>

      {/* Legend as status badges */}
      <div className="grid grid-cols-2 gap-2 mt-4">
        {(Object.keys(byStatus) as ComplaintStatus[]).map((status) => {
          const value = byStatus[status];
          const percent = total > 0 ? Math.round((value / total) * 100) : 0;

          return (
            <div
              key={status}
              className={`flex items-center justify-between px-3 py-2 rounded-lg ${STATUS_BG_COLORS[status]} transition-transform hover:scale-[1.02]`}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: STATUS_COLORS[status] }}
                />
                <span className={`text-xs font-medium ${STATUS_TEXT_COLORS[status]}`}>
                  {STATUS_LABELS[status]}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`text-sm font-semibold ${STATUS_TEXT_COLORS[status]}`}>
                  {value}
                </span>
                <span className="text-xs text-gray-400">
                  ({percent}%)
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
